// src/App.js
import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate

} from 'react-router-dom';

import AdminLayout from './admin/layouts/AdminLayout';
import { AdminAuthProvider } from './admin/auth/AdminAuthContext';
import AdminNetworkErrorPage from './admin/pages/errors/AdminNetworkError';
import useDocumentTitle from './shared/hooks/useDocumentTitle';
import UserLayout from './user/layouts/UserLayout';
import RootPage from './user/pages/RootPage';
import RedirectTo from './shared/components/RedirectTo';
import WorkflowScreenAdmin from './WorkflowScreenAdmin';
import WorkflowScreenUser from './user/pages/WorkflowScreenUser';
import FlowDiagram from './admin/pages/FlowDiagram';
import Asset from './user/pages/Asset';
import AssetAiChatPage from './user/pages/AssetAiChatPage';
import LaunchWorkflowPage from './user/pages/LaunchWorkflowPage';
import UserWorkflowThankYouPage from './user/pages/UserWorkflowThankYouPage';
import AdvancedAssetUser from './user/pages/AdvancedAssetUser';
import AdminDashboard from './admin/pages/AdminDashboard';
import WorkflowsPage from './admin/pages/WorkflowsPage';
import AssetsPage from './admin/pages/AssetsPage';
import AssetDetailsPage from './admin/pages/AssetDetailsPage';
import TasksPage from './admin/pages/AdminTasks';
import AdminLoginScreen from './admin/pages/AdminLoginScreen';
import RequireAdminAuth from './admin/auth/RequireAdminAuth';
import UsersPage from './admin/pages/UsersPage';
import ReleasesPage from './admin/pages/ReleasesPage';
import InvitationAcceptPage from './admin/pages/InvitationAcceptPage';
import PasswordResetRequestPage from './admin/auth/PasswordResetRequestPage';
import PasswordResetPage from './admin/auth/PasswordResetPage';
import RequirePermission from './admin/auth/RequirePermission';
import AdminReportsTasksPage from './admin/pages/AdminReportsTasksPage.jsx';
import AccessUrlsPage from './admin/pages/AccessUrlsPage';
import KitsPage from './admin/pages/KitsPage';
import KitBatchesPage from './admin/pages/KitBatchesPage';
import ClaimUrlPage from './admin/pages/ClaimUrlPage';
import WelcomePage from './admin/pages/WelcomePage';
import RegisterPage from './admin/pages/RegisterPage';
import OrganizationFormsPage from './admin/pages/OrganizationFormsPage';
import NavGroupLandingPage from './admin/pages/NavGroupLandingPage';
import { orgSettingsItems } from './admin/components/navConfig';
import { can } from './admin/auth/permissions';
import OrganizationAiPage from './admin/pages/OrganizationAiPage';
import OrganizationIntegrationsPage from './admin/pages/OrganizationIntegrationsPage';
import OrganizationApiTokensPage from './admin/pages/OrganizationApiTokensPage';
import OrganizationMediaLibraryPage from './admin/pages/OrganizationMediaLibraryPage';
import OrganizationDocumentLibraryPage from './admin/pages/OrganizationDocumentLibraryPage';
import TagsPage from './admin/pages/TagsPage';
import FormEditPage from './admin/pages/FormEditPage';

import NotFound from './shared/pages/NotFound';

import { ReactFlowProvider } from '@xyflow/react';
import { UserProfileProvider } from './user/contexts/UserProfileContext';
import { AccessProvider } from './user/contexts/AccessProvider';
import { WorkflowCacheProvider } from './user/contexts/WorkflowCacheContext';

import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/bootstrap-custom.scss'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; 
import { CurrentUserProvider, useCurrentUser } from './admin/auth/CurrentUserContext';
import { IntegrationsProvider } from './admin/integrations/IntegrationsContext';
import { setNavigate } from './shared/services/NavigationService';

function AppNavigationBinder() {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
}


// The Organization settings group as a page. Its children are role-filtered, so the list cannot
// be a constant: an admin sees seven cards, and a role that loses all but one is redirected
// straight into that one by NavGroupLandingPage.
function OrganizationSettingsLanding() {
  const { user } = useCurrentUser();
  const items = orgSettingsItems.filter(
    item => !item.requiredAction || can(user, item.requiredAction)
  );

  return (
    <NavGroupLandingPage
      title="Organization settings"
      description="Everything configured once for the whole organization."
      items={items}
    />
  );
}

function App() {
  useDocumentTitle('Maintella – empower your field engineers');
  return (
    <UserProfileProvider>
      <AccessProvider>
        <CurrentUserProvider>
          <AdminAuthProvider>
            <Router>
              <AppNavigationBinder />

              <Routes>
              <Route path="/admin/login/" element={<AdminLoginScreen />} />
              <Route path="/admin/register" element={<RegisterPage />} />
              <Route path="/invite/accept" element={<InvitationAcceptPage />} />
              <Route path="/admin/password/forgot" element={<PasswordResetRequestPage />} />
              <Route path="/admin/password/reset" element={<PasswordResetPage />} />

              <Route path="/admin" element={
                <RequireAdminAuth>
                  <IntegrationsProvider>
                    <AdminLayout />
                  </IntegrationsProvider>
                </RequireAdminAuth>
              }>

                <Route path="/admin/error/network" element={<AdminNetworkErrorPage />} />
                <Route index element={<RedirectTo to="./dashboard" />} />
                <Route path="/admin/dashboard/" element={<AdminDashboard />} />
                <Route path="/admin/welcome" element={<WelcomePage />} />
                <Route path="/admin/assets/" element={<AssetsPage />} />
                <Route path="/admin/assets/:asset_id" element={<AssetDetailsPage />} />
                <Route path="/admin/tasks" element={<TasksPage />} />
                <Route path="/admin/short-urls" element={<AccessUrlsPage />} />
                <Route path="/admin/short-urls/claim/:code" element={<ClaimUrlPage />} />
                <Route path="/admin/kits" element={<KitsPage />} />
                <Route path="/admin/kit-batches" element={<KitBatchesPage />} />
                <Route path="/admin/users" element={
                  <RequirePermission action="users.list">
                    <UsersPage />
                  </RequirePermission>
                } />
                <Route path="/admin/releases" element={<ReleasesPage />} />
                <Route path="/admin/flows/" element={<WorkflowsPage />} />
                <Route path="/admin/reports/tasks" element={
                  <RequirePermission action="reports.read">
                    <AdminReportsTasksPage />
                  </RequirePermission>
                } />
                {/* The group's own page: one card per settings page. Reached from the
                    collapsed sidebar rail, which has nowhere to open a submenu. The visible
                    children depend on the role, so the list is filtered here rather than
                    hard-coded, and the page redirects when there is nothing to choose. */}
                <Route path="/admin/organization" element={
                  <RequirePermission action="organization.settings">
                    <OrganizationSettingsLanding />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/forms" element={
                  <RequirePermission action="organization.settings">
                    <OrganizationFormsPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/media-library" element={
                  <RequirePermission action="organization.settings">
                    <OrganizationMediaLibraryPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/document-library" element={
                  <RequirePermission action="organization.settings">
                    <OrganizationDocumentLibraryPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/tags" element={
                  <RequirePermission action="tags.manage">
                    <TagsPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/ai/:tab" element={
                  <RequirePermission action="organization.settings">
                    <OrganizationAiPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/ai" element={
                  <Navigate to="/admin/organization/ai/field-engineer" replace />
                } />
                <Route path="/admin/organization/ai-indexing" element={
                  <Navigate to="/admin/organization/ai/indexing" replace />
                } />
                <Route path="/admin/organization/ai-settings" element={
                  <Navigate to="/admin/organization/ai/field-engineer" replace />
                } />
                <Route path="/admin/organization/field-engineer-ai" element={
                  <Navigate to="/admin/organization/ai/field-engineer" replace />
                } />
                <Route path="/admin/organization/integrations" element={
                  <RequirePermission action="integrations.manage">
                    <OrganizationIntegrationsPage />
                  </RequirePermission>
                } />
                <Route path="/admin/organization/api-tokens" element={
                  <RequirePermission action="api_tokens.manage">
                    <OrganizationApiTokensPage />
                  </RequirePermission>
                } />
                <Route path="/admin/forms/:id/edit" element={
                  <RequirePermission action="organization.settings">
                    <FormEditPage />
                  </RequirePermission>
                } />
                <Route
                  path="/admin/flow/new"
                  element={
                    <ReactFlowProvider>
                      <FlowDiagram />
                    </ReactFlowProvider>
                  }
                />
                <Route
                  path="/admin/flow/:flow_id/edit/"
                  element={
                    <ReactFlowProvider>
                      <FlowDiagram />
                    </ReactFlowProvider>
                  }
                />
              </Route>
              <Route
                path="/admin/flow/:flow_id/preview"
                element={
                  <RequireAdminAuth>
                    <UserLayout />
                  </RequireAdminAuth>
                }
              >
                <Route path="/admin/flow/:flow_id/preview" element={<RedirectTo to="./start" />} />
                <Route path="/admin/flow/:flow_id/preview/:viewId" element={<WorkflowScreenAdmin />} />
              </Route>
                
              <Route path="/" element={
                <WorkflowCacheProvider>
                  <UserLayout />
                </WorkflowCacheProvider>
              }>
                <Route path="/" element={<RootPage />} />
                <Route path="/flow/:flow_id" element={<RedirectTo to="./start" />} />
                

                <Route path="/assets/access_key/:access_key/thank-you" element={<UserWorkflowThankYouPage />} />
                <Route path="/assets/access_key/:access_key" element={<Asset />} />
                <Route path="/assets/access_key/:access_key/chat" element={<AssetAiChatPage />} />
                <Route path="/assets/access_key/:access_key/launch/:workflow_id" element={<LaunchWorkflowPage />} />
                <Route path="/assets/access_key/:access_key/advanced" element={<AdvancedAssetUser />} />
                <Route path="/assets/access_key/:access_key/task/:task_id" element={<RedirectTo to="./start" />} />
                <Route path="/assets/access_key/:access_key/task/:task_id/:view_id" element={<WorkflowScreenUser />} /> 

                
                

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </Router>
          </AdminAuthProvider>
        </CurrentUserProvider>
      </AccessProvider>
    </UserProfileProvider>
  );
}

export default App;
